<template>
  <v-card class="rounded-lg elevation-3" height="90vh">
    <v-card-title class="headline primary white--text py-4 d-flex justify-space-between">
      <span class="d-flex align-center">
        <v-icon left large color="white">mdi-view-grid</v-icon>
        Catálogo de Artículoss
      </span>
      <v-btn icon @click="$emit('close-item')" class="white--text">
        <v-icon>mdi-close</v-icon>
      </v-btn>
    </v-card-title>

    <v-card-text class="pa-4">
      <v-row>
        <v-col cols="12" sm="4" md="3">
          <v-card outlined class="mb-4 filter-card">
            <v-card-title class="subtitle-1">
              <v-icon left color="primary">mdi-filter-variant</v-icon>
              Filtros
            </v-card-title>
            <v-card-text>
              <v-text-field v-model="searchInput" label="Buscar por descripcion o código"
                prepend-inner-icon="mdi-magnify" clearable outlined dense :loading="searchLoading" hide-details
                class="mb-4" @input="onSearchInput"></v-text-field>

              <v-autocomplete v-model="selectedCategory" :items="categoriesWithAll" item-text="name" item-value="id"
                label="Categoría" outlined dense hide-details class="mb-4" @change="onFilterChange"></v-autocomplete>

              <v-autocomplete v-if="showWarehouse && !search_in_requests" v-model="selectedWarehouse"
                :items="warehousesWithAll" item-text="name" item-value="id" label="Almacén" outlined dense hide-details
                @change="onFilterChange"></v-autocomplete>

              <v-autocomplete v-if="showWarehouse && search_in_requests" v-model="selectedWarehouse"
                :items="itemsWarehouse" color="cyanText" label="Almacen" item-value="id" item-text="name"
                prepend-inner-icon="business" outlined required dense @change="get_items_request"></v-autocomplete>
            </v-card-text>
          </v-card>

          <!-- Información de paginación -->
          <v-card outlined class="mb-4" v-if="!search_in_requests">
            <v-card-text class="py-2">
              <div class="caption text-center">
                <strong>{{ totalItems }}</strong> artículos encontrados
                <br />
                Página {{ currentPage }} de {{ totalPages }}
              </div>
            </v-card-text>
          </v-card>
        </v-col>

        <v-col cols="12" sm="8" md="9">
          <v-data-table :headers="headers" :items="items" :loading="loading" :server-items-length="totalItems"
            :options.sync="tableOptions" :footer-props="{
              'items-per-page-options': [10, 20, 50],
              'items-per-page-text': 'Artículos por página',
              'show-current-page': true,
              'show-first-last-page': true,
            }" height="calc(90vh - 170px)" fixed-header item-key="item_id" class="elevation-1" disable-sort>
            <template v-slot:item.image="{ item }">
              <v-avatar size="40" class="ma-2">
                <v-img :src="item.item_image" :alt="item.item_name"></v-img>
              </v-avatar>
            </template>

            <template v-if="showWarehouse && !search_in_requests" v-slot:item.warehouse="{ item }">
              <v-autocomplete v-model="item.warehouse_discount" :items="getWarehouse(item)" item-text="name"
                item-value="id" label="Almacén" outlined dense hide-details return-object
                @change="updateItemWarehouse(item)"></v-autocomplete>
            </template>

            <template v-slot:item.quantity="{ item }">
              <v-chip :color="getQuantityColor(item)" text-color="white" small label>
                {{ item.warehouse_discount ? item.warehouse_discount.quantity : 0 }}
              </v-chip>
            </template>

            <template v-slot:item.price="{ item }">
              <span class="font-weight-bold">{{ currencyFormat(getPrice(item)) }}</span>
            </template>

            <template v-slot:item.actions="{ item }">
              <v-tooltip bottom>
                <template v-slot:activator="{ on, attrs }">
                  <v-btn color="primary" small @click="selectItem(item)" :disabled="!canSelectItem(item)" v-bind="attrs"
                    v-on="on">
                    <v-icon left small>mdi-plus</v-icon>
                    Agregar
                  </v-btn>
                </template>
                <span>{{ getTooltipText(item) }}</span>
              </v-tooltip>
            </template>

            <template v-slot:no-data>
              <v-alert type="info" class="ma-2">
                <div v-if="!loading">
                  No se encontraron artículos
                  <div v-if="searchInput || selectedCategory !== 'all'" class="mt-2">
                    <v-btn small text @click="clearFilters">
                      <v-icon left small>mdi-filter-remove</v-icon>
                      Limpiar filtros
                    </v-btn>
                  </div>
                </div>
              </v-alert>
            </template>

            <template v-slot:progress>
              <v-progress-linear indeterminate color="primary"></v-progress-linear>
            </template>
          </v-data-table>
        </v-col>
      </v-row>
    </v-card-text>

    <v-snackbar v-model="snackbar" :color="snackbarColor" top>
      {{ snackbarText }}
      <template v-slot:action="{ attrs }">
        <v-btn text v-bind="attrs" @click="snackbar = false"> Cerrar </v-btn>
      </template>
    </v-snackbar>
  </v-card>
</template>

<script>
import moment from "moment";

export default {
  name: "ArticlesCatalogue",

  data: () => ({
    items: [],
    loading: true,
    searchInput: "",
    searchTimeout: null,
    itemsWarehouse: [],
    headers: [
      { text: "ID", align: "start", value: "item_id", width: "5%" },
      { text: "Imagen", value: "image", width: "8%" },
      { text: "Descripción", value: "item_name" },
      { text: "Unidad", value: "unit_name", width: "8%", align: "center" },
      { text: "Almacén", value: "warehouse", width: "15%", align: "center" },
      { text: "Cantidad", value: "quantity", width: "8%", align: "center" },
      { text: "Precio", value: "price", width: "10%", align: "end" },
      { text: "Acciones", value: "actions", sortable: false, width: "8%" },
    ],
    selectedCategory: "all",
    selectedWarehouse: "all",
    categories: [],
    categoryTree: [],
    snackbar: false,
    snackbarText: "",
    snackbarColor: "success",

    // Paginación
    currentPage: 1,
    itemsPerPage: 10,
    totalItems: 0,
    totalPages: 0,
    hasNextPage: false,
    hasPrevPage: false,

    // Opciones de la tabla
    tableOptions: {
      page: 1,
      itemsPerPage: 10,
    },
    lastRequestId: 0,
    searchLoading: false,
    abortController: null,
  }),

  props: {
    data: {
      type: Object,
      required: true,
    },
    showWarehouse: {
      type: Boolean,
      default: true,
    },
    search_in_requests: {
      type: Boolean,
      default: false,
    },
    removeItemSelected: {
      type: Boolean,
      default: true,
    },
    selected_currencies: {
      type: Number,
    },
    itbis: {
      type: Array,
      required: true,
    },
    type: {
      type: Number,
      default: 1,
      validator: (value) => [1, 2].includes(value),
    },
  },

  computed: {
    categoriesWithAll() {
      return [{ id: "all", name: "Todas las categorías" }, ...this.categories];
    },
    warehousesWithAll() {
      return [{ id: "all", name: "Todos los almacenes" }, ...this.itemsWarehouse];
    },
  },

  watch: {
    tableOptions: {
      handler(newOptions) {
        if (!this.search_in_requests) {
          this.currentPage = newOptions.page;
          this.itemsPerPage = newOptions.itemsPerPage;
          this.fetchInventoryData();
        }
      },
      deep: true,
    },
  },

  created() {
    this.init();
  },

  methods: {
    // Manejo del input de búsqueda con debounce
    onSearchInput() {
      clearTimeout(this.searchTimeout);
      this.searchLoading = true;
      this.searchTimeout = setTimeout(() => {
        this.onFilterChange();
      }, 300); // Reducido a 300ms para una respuesta inmediata
    },

    // Cuando cambian los filtros, resetear a página 1
    onFilterChange() {
      this.currentPage = 1;
      this.tableOptions.page = 1;
      this.fetchInventoryData();
    },

    // Limpiar filtros
    clearFilters() {
      this.searchInput = "";
      this.selectedCategory = "all";
      this.selectedWarehouse = "all";
      this.onFilterChange();
    },

    getPrice(item) {
      if (!this.selected_currencies || !item.item_currencies?.length) {
        return 0;
      }

      const currency = item.item_currencies.find(
        (element) => element.currency_id === this.selected_currencies
      );
      if (!currency) return 0;

      const itbis = parseFloat(
        this.itbis.find((element) => element.id === item.tax_type_id)?.value || 0
      );

      const basePrice = this.type === 1 ? currency.cost_price : currency.sale_price;
      const totalPrice = basePrice * (1 + itbis);

      if (this.type === 1) {
        item.cost_price = totalPrice;
      } else {
        item.sale_price = totalPrice;
      }

      return totalPrice;
    },

    selectItem(item) {
      if (item.is_internal) return;
      this.$emit("selected-item", item);

      if (this.removeItemSelected) {
        // En lugar de remover del array local, refrescar los datos
        // para mantener la consistencia con el servidor
        this.fetchInventoryData();
      }

      this.showSnackbar("Artículo agregado correctamente", "success");
    },

    getWarehouse(items) {
      const warehouses = items.item_in_warehouses
        .map((ware) => {
          const matchingWarehouse = this.itemsWarehouse.find(
            (itWare) => ware.warehouse_id === itWare.id
          );
          if (matchingWarehouse) {
            return {
              id: ware.warehouse_id,
              name: matchingWarehouse.name,
              quantity: parseFloat(ware.quantity) || 0,
            };
          }
          return null;
        })
        .filter(Boolean);

      // Ordenar por cantidad descendente para que el primero sea el de mayor cantidad
      return warehouses.sort((a, b) => b.quantity - a.quantity);
    },

    // Método para seleccionar automáticamente el almacén con mayor cantidad
    selectWarehouseWithMaxQuantity(item) {
      const warehouses = this.getWarehouse(item);
      if (warehouses.length > 0) {
        // Buscar el almacén con mayor cantidad
        const maxQuantityWarehouse = warehouses[0];

        // Si el almacén con mayor cantidad tiene stock > 0, seleccionarlo
        if (maxQuantityWarehouse.quantity > 0) {
          return maxQuantityWarehouse;
        }

        // Si todos los almacenes tienen cantidad 0, seleccionar el primero
        return warehouses[0];
      }
      return null;
    },

    async getCategories() {
      if (!this.data.business) {
        console.warn("[ArticlesWarehouse] Skip getCategories: No business unit ID");
        return;
      }

      try {
        let filters = `where ITEM_CATEGORIES.BUSINESS_UNIT_ID=${this.data.business}`;

        if (this.data.location) {
          filters += ` AND LOCATION_ID=${this.data.location}`;
        }

        const resultCategories = await this.api_Post("inventory/GetCategories", {
          filters,
        });

        if (!resultCategories.ok) {
          throw new Error(`HTTP error! status: ${resultCategories.status}`);
        }

        const data = await resultCategories.json();

        const formattedCategories = data.map((item) => ({
          id: item.id,
          parent: item.parent_category_id,
          name: item.name,
          note: item.note,
          business_unit_id: item.business_unit_id,
          bussines_unit_description: item.bussines_unit_description,
          location_id: item.location_id,
          location_description: item.location_description,
          code: item.code,
          account_type_id: item.account_type_id,
          dgii_category_id: item.dgii_category_id,
          active: item.active,
        }));

        this.categoryTree = this.nestCategories(formattedCategories);
        this.categories = this.flattenCategoryTree(this.categoryTree);
      } catch (error) {
        console.error("Error fetching categories:", error);
        this.showSnackbar("Error al cargar las categorías", "error");
      }
    },

    nestCategories(items, id = null, link = "parent") {
      return items
        .filter((item) => item[link] === id)
        .map((item) => ({ ...item, children: this.nestCategories(items, item.id) }));
    },

    flattenCategoryTree(tree, prefix = "") {
      return tree.reduce((acc, category) => {
        const flatCategory = {
          id: category.id,
          name: prefix + category.name,
        };
        acc.push(flatCategory);

        if (category.children && category.children.length > 0) {
          acc.push(
            ...this.flattenCategoryTree(category.children, `${prefix}${category.name} > `)
          );
        }

        return acc;
      }, []);
    },

    async init() {
      try {
        this.loading = true;
        if (this.search_in_requests) {
          this.selectedWarehouse = this.data.warehouse;
          await this.get_items_request();
        } else {
          await this.getCategories();
          await this.fetchWarehouses();
          await this.fetchInventoryData();
        }
      } catch (error) {
        console.error("Error initializing data:", error);
        this.showSnackbar(
          error.message || "An error occurred while loading data",
          "error"
        );
      } finally {
        this.loading = false;
      }
    },
    async get_items_request() {
      if (!this.data.business || !this.data.location || !this.selectedWarehouse) {
        console.warn("[ArticlesWarehouse] Skip get_items_request: Missing parameters", {
          business: this.data.business,
          location: this.data.location,
          warehouse: this.selectedWarehouse
        });
        return;
      }

      // Cancelar petición previa si existe
      if (this.abortController) {
        this.abortController.abort();
      }
      this.abortController = new AbortController();

      this.lastRequestId++;
      const currentRequestId = this.lastRequestId;
      this.searchLoading = true;

      let filtros = ` WHERE difference > 0 AND REQUEST_WITH_DETAILS.BUSINESS_UNIT_ID=${this.data.business} AND REQUEST_WITH_DETAILS.LOCATION_ID=${this.data.location} AND WAREHOUSE_ID=${this.selectedWarehouse}`;

      try {
        const response = await this.safeApiCall('POST', 'shopping/articles_for_quotes', { filters: filtros }, this.abortController.signal);
        
        const data = await response.json();

        if (this.lastRequestId !== currentRequestId) return;

        if (response.status == 200) {
          this.items = data;
          for (let key in this.items) {
            this.items[key].warehouse_discount = { quantity: parseFloat(this.items[key].difference) };
            this.items[key].requested_quantity = parseFloat(this.items[key].quantity);
          }
          if (this.itemsWarehouse.length == 0) {
            const warehousesResponse = await this.api_Get(
              `inventory/warehouseForLocation/${this.data.location}`
            );
            this.itemsWarehouse = await warehousesResponse.json();
          }
        } else {
          this.$toast.error("Error: " + data.error);
        }
      } catch (error) {
        if (error.name === 'AbortError') return;
        if (this.lastRequestId === currentRequestId) {
          this.$toast.error("Error: " + error);
        }
      } finally {
        if (this.lastRequestId === currentRequestId) {
          this.searchLoading = false;
        }
      }
    },

    async fetchWarehouses() {
      try {
        if (!this.data.location || this.data.location === 'undefined') {
          console.warn("[ArticlesWarehouse] Skip fetchWarehouses: No location ID");
          return;
        }

        const warehousesResponse = await this.api_Get(
          `inventory/warehouseForLocation/${this.data.location}`
        );
        this.itemsWarehouse = await warehousesResponse.json();
      } catch (error) {
        console.error("Error fetching warehouses:", error);
        this.showSnackbar("Error al cargar los almacenes", "error");
      }
    },

    async safeApiCall(method, endpoint, data = null, signal = null) {
      const baseUrl = process.env.NODE_ENV === 'production'
        ? process.env.VUE_APP_API_URL_PRODUCTION
        : process.env.VUE_APP_API_URL_DEV;

      const config = {
        method,
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${this.$store.state.auth.token}`
        },
        signal
      };

      if (data && method === 'POST') {
        config.body = JSON.stringify(data);
      }

      return await fetch(baseUrl + endpoint, config);
    },

    async fetchInventoryData() {
      if (this.search_in_requests) return;

      this.lastRequestId++;
      const currentRequestId = this.lastRequestId;

      // Cancelar petición previa si existe
      if (this.abortController) {
        this.abortController.abort();
      }
      this.abortController = new AbortController();

      try {
        this.searchLoading = true;
        // Solo mostramos el loading general si la tabla está vacía para evitar flicker
        if (this.items.length === 0) {
          this.loading = true;
        }

        if (!this.data.business || !this.data.location) {
          console.warn("[ArticlesWarehouse] Skip fetchInventoryData: Missing business or location");
          this.searchLoading = false;
          this.loading = false;
          return;
        }

        const requestBody = {
          date: moment().format("YYYY-MM-DD"),
          business_unit_id: this.data.business,
          location_id: this.data.location,
          page: this.currentPage,
          limit: this.itemsPerPage,
          search: this.searchInput || "",
          category_id: this.selectedCategory !== "all" ? this.selectedCategory : null,
          warehouse_id: this.selectedWarehouse !== "all" ? this.selectedWarehouse : null,
          is_now: true,
        };

        const response = await this.safeApiCall('POST', 'inventory/inventory_value', requestBody, this.abortController.signal);
        const responseData = await response.json();

        if (this.lastRequestId !== currentRequestId) return;

        if (response.status === 200) {
          // Procesar los datos
          this.items = responseData.data.map((item) => {
            const processedItem = {
              ...item,
              item_image: item.image_json
                ? this.get_storage(item.image_json)
                : "image-not-available.jpg",
            };

            // Seleccionar automáticamente el almacén con mayor cantidad
            processedItem.warehouse_discount = this.selectWarehouseWithMaxQuantity(
              processedItem
            );

            return processedItem;
          });

          // Actualizar información de paginación
          if (responseData.pagination) {
            this.currentPage = responseData.pagination.current_page;
            this.itemsPerPage = responseData.pagination.per_page;
            this.totalItems = responseData.pagination.total_items;
            this.totalPages = responseData.pagination.total_pages;
            this.hasNextPage = responseData.pagination.has_next_page;
            this.hasPrevPage = responseData.pagination.has_prev_page;
          }
        } else {
          throw new Error(responseData.error || "Error al cargar los datos");
        }
      } catch (error) {
        if (error.name === 'AbortError') return;
        if (this.lastRequestId === currentRequestId) {
          console.error("Error fetching inventory data:", error);
          this.showSnackbar(error.message || "Error al cargar el inventario", "error");
          this.items = [];
          this.totalItems = 0;
          this.totalPages = 0;
        }
      } finally {
        if (this.lastRequestId === currentRequestId) {
          this.loading = false;
          this.searchLoading = false;
        }
      }
    },

    getQuantityColor(item) {
      const quantity = item.warehouse_discount ? item.warehouse_discount.quantity : 0;
      if (quantity > 10) return "green";
      if (quantity > 5) return "orange";
      return "red";
    },

    updateItemWarehouse(item) {
      // Actualizar la información del almacén seleccionado
      if (item.warehouse_discount) {
        this.showSnackbar(
          `Almacén "${item.warehouse_discount.name}" seleccionado`,
          "info"
        );
      }
    },

    canSelectItem(item) {
      // Los artículos internos del sistema no se pueden solicitar
      if (item.is_internal) return false;
      if (this.type == 1) {
        return true;
      }

      return (
        item.negative_sale ||
        item.item_type_id === 2 ||
        (item.warehouse_discount != null && item.warehouse_discount.quantity > 0)
      );
    },

    getTooltipText(item) {
      if (item.is_internal) {
        return "Artículo interno del sistema: no se puede solicitar";
      } else if (item.negative_sale) {
        return "Este artículo permite ventas negativas";
      } else if (item.item_type_id === 2) {
        return "Este es un servicio y puede ser vendido";
      } else if (
        item.warehouse_discount != null &&
        item.warehouse_discount.quantity > 0
      ) {
        return "Este artículo está disponible en el inventario";
      } else {
        return "Este artículo no está disponible para la venta";
      }
    },

    showSnackbar(text, color = "success") {
      this.snackbarText = text;
      this.snackbarColor = color;
      this.snackbar = true;
    },
  },
};
</script>

<style scoped>
.v-data-table ::v-deep .v-data-table__wrapper {
  overflow-y: auto;
}

.filter-card {
  background-color: #f5f5f5;
  border-radius: 8px;
}

.v-card__title {
  font-size: 1.25rem;
  font-weight: 500;
}

.v-chip {
  font-weight: 500;
}

.v-btn {
  text-transform: none;
}

.v-data-table ::v-deep .v-data-table__progress {
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
}

/* Estilos para la paginación */
.v-data-table ::v-deep .v-data-footer {
  border-top: 1px solid #e0e0e0;
  background-color: #fafafa;
}

.v-data-table ::v-deep .v-data-footer__pagination {
  margin: 0 8px;
}

.v-data-table ::v-deep .v-data-footer__select {
  margin: 0 8px;
}

/* Mejorar la apariencia de los filtros */
.filter-card .v-card__text {
  padding: 16px;
}

.filter-card .v-input {
  margin-bottom: 8px;
}

/* Estilo para el indicador de carga */
.v-data-table ::v-deep .v-data-table__progress th {
  height: 4px !important;
  border: none !important;
  padding: 0 !important;
}
</style>
